import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { Baseurl } from '../../config/BaseUrl';

const initialState = {
  CartItems: [],
  selectedCategory: ' ',
  ProductTotal: [],
  cartTotalQuantity: 0,
  cartTotalAmount: 0,
  netPayable: 0,
  cartTotalMrp: 0,
  cartTotalPrice: 0,
  cartPackQuantity: 0,
  CouponDiscountAmount: 0,
  logisticsAmount: 0,
  Cartloading: true,
  productUpdated: false,
  checkoutData: '',
  loading: false,
  error: null,
};

// Helper functions for localStorage (Web)
const getLocalStorage = (key) => {
  if (typeof window !== 'undefined') {
    try {
      return JSON.parse(localStorage.getItem(key)) || null;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  }
  return null;
};

const setLocalStorage = (key, value) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  }
};

// Async Thunks for API calls
export const fetchCartFromBackend = createAsyncThunk(
  'cart/fetchCartFromBackend',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log('🛒 Fetching cart - clientId:', clientId);

      // Check if user is authenticated
      if (!clientId) {
        console.log('🛒 No authenticated user, skipping cart fetch');
        return {
          success: true,
          data: {
            CartItems: [],
            cartTotalAmount: 0,
            netPayable: 0,
            cartTotalMrp: 0,
            cartTotalQuantity: 0,
            cartPackQuantity: 0,
          },
        };
      }

      console.log('🛒 Making API call to fetch cart for client:', clientId);

      // ✅ Send clientId as query parameter for GET request
      const response = await axios.get(
        `${Baseurl}/api/v1/cart/shop/all?clientId=${clientId}`,
      );

      console.log('✅ Cart API response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Cart fetch error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addToCartBackend = createAsyncThunk(
  'cart/addToCartBackend',
  async (itemData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log('🛒 Adding to cart - clientId:', clientId);
      console.log('🛒 Item data:', JSON.stringify(itemData, null, 2));

      if (!clientId) {
        return rejectWithValue('No authenticated user');
      }

      // ✅ Prepare the data with clientId in request body for POST request
      const cartData = {
        ...itemData,
        clientId: clientId,
      };

      console.log('🛒 Sending to backend:', JSON.stringify(cartData, null, 2));

      const response = await axios.post(
        `${Baseurl}/api/v1/cart/shop/new`,
        cartData,
      );

      console.log('✅ Add to cart successful:', response.data);
      return response.data;
    } catch (error) {
      console.log(
        '❌ Add to cart error:',
        error.response?.data || error.message,
      );
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const removeFromCartBackend = createAsyncThunk(
  'cart/removeFromCartBackend',
  async (productId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log(
        '🛒 Removing from cart - clientId:',
        clientId,
        'productId:',
        productId,
      );

      if (!clientId) {
        return rejectWithValue('No authenticated user');
      }

      // ✅ Send clientId as query parameter for DELETE request
      const response = await axios.delete(
        `${Baseurl}/api/v1/cart/shop/remove/${productId}?clientId=${clientId}`,
      );

      console.log('✅ Remove from cart successful:', response.data);
      return response.data;
    } catch (error) {
      console.log('❌ Remove from cart error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateCartItemBackend = createAsyncThunk(
  'cart/updateCartItemBackend',
  async ({ productId, quantity }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log(
        '🛒 Updating cart item - clientId:',
        clientId,
        'productId:',
        productId,
        'quantity:',
        quantity,
      );

      if (!clientId) {
        return rejectWithValue('No authenticated user');
      }

      // ✅ Send clientId in request body for PUT request
      const response = await axios.put(
        `${Baseurl}/api/v1/cart/shop/update/${productId}`,
        {
          quantity,
          clientId,
        },
      );

      console.log('✅ Update cart item successful:', response.data);
      return response.data;
    } catch (error) {
      console.log('❌ Update cart item error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const clearCartBackend = createAsyncThunk(
  'cart/clearCartBackend',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const clientId = state.Authentication.clientid;

      console.log('🛒 Clearing cart - clientId:', clientId);

      if (!clientId) {
        return rejectWithValue('No authenticated user');
      }

      // ✅ Send clientId as query parameter for DELETE request
      const response = await axios.delete(
        `${Baseurl}/api/v1/cart/shop/clear?clientId=${clientId}`,
      );

      console.log('✅ Clear cart successful:', response.data);
      return response.data;
    } catch (error) {
      console.log('❌ Clear cart error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const CartSlice = createSlice({
  name: 'Cart',
  initialState,
  reducers: {
    allProductUpdate(state, action) {
      state.ProductTotal = action.payload;
      state.productUpdated = true;
      setLocalStorage('ProductTotal', state.ProductTotal);
    },
    setSelectedCategory(state, action) {
      state.selectedCategory = action.payload;
      setLocalStorage('selectedCategory', state.selectedCategory);
    },
    setCheckoutAllData(state, action) {
      state.checkoutData = action.payload;
    },
    // Local cart actions (for immediate UI update)
    addToCartLocal(state, action) {
      const item = action.payload;
      let itemIndex = -1;
      itemIndex = state.CartItems.findIndex(
        cartItem => cartItem.ProductId === item.ProductId,
      );

      if (itemIndex >= 0) {
        state.cartTotalQuantity++;
        const existingItem = state.CartItems[itemIndex];
        existingItem.Qty += 1;
        existingItem.TotalMrp = existingItem.Mrp * existingItem.Qty;
        existingItem.TotalPrice = existingItem.Price * existingItem.Qty;
        existingItem.TotalAmount = existingItem.Price * existingItem.Qty;

        // PRESERVE SALON NAME WHEN UPDATING EXISTING ITEM
        if (item.SalonName) {
          existingItem.SalonName = item.SalonName;
        }

        state.cartTotalAmount += existingItem.Price;
        state.netPayable = state.cartTotalAmount;
      } else {
        const tempProduct = {
          ...item,
          // MAKE SURE SALON NAME IS INCLUDED WHEN ADDING NEW ITEM
          SalonName: item.SalonName,
        };
        state.CartItems.push(tempProduct);
        state.cartTotalQuantity++;
        state.cartPackQuantity++;
        state.cartTotalAmount += tempProduct.Price;
        state.netPayable = state.cartTotalAmount;
      }
      setLocalStorage('CartItems', state.CartItems);
    },
    decrementCartLocal(state, action) {
      const item = action.payload;
      let itemIndex = -1;
      itemIndex = state.CartItems.findIndex(
        cartItem => cartItem.ProductId === item.ProductId,
      );

      if (itemIndex >= 0) {
        if (state.CartItems[itemIndex].Qty > 1) {
          let quant = state.CartItems[itemIndex].Qty - 1;
          state.CartItems[itemIndex].Qty = state.CartItems[itemIndex].Qty - 1;
          state.CartItems[itemIndex].TotalMrp =
            Number(state.CartItems[itemIndex].Mrp) * Number(quant);
          state.CartItems[itemIndex].TotalPrice =
            Number(state.CartItems[itemIndex].Price) * Number(quant);
          state.CartItems[itemIndex].TotalAmount =
            Number(state.CartItems[itemIndex].Price) * Number(quant);

          // PRESERVE SALON NAME
          if (item.SalonName) {
            state.CartItems[itemIndex].SalonName = item.SalonName;
          }
        } else if (state.CartItems[itemIndex].Qty === 1) {
          const nextCartItems = state.CartItems.filter(
            cartItem => cartItem.ProductId !== action.payload.ProductId,
          );
          state.cartPackQuantity--;
          state.CartItems = nextCartItems;
        }

        state.cartTotalAmount -= action.payload.Price;
        state.netPayable = state.cartTotalAmount;
      }
      setLocalStorage('CartItems', state.CartItems);
    },
    removeFromCartLocal(state, action) {
      const nextCartItems = state.CartItems.filter(
        cartItem => cartItem.ProductId !== action.payload.ProductId,
      );
      state.CartItems = nextCartItems;
      setLocalStorage('CartItems', state.CartItems);
    },
    getCartTotal(state, action) {
      state.cartTotalAmount = 0;
      state.netPayable = 0;
      state.cartTotalMrp = 0;
      state.cartTotalPrice = 0;
      const totalitem = state.CartItems;

      for (let i = 0; i < totalitem.length; i++) {
        state.cartTotalAmount += Number(state.CartItems[i].TotalAmount);
        state.cartTotalMrp += Number(state.CartItems[i].TotalMrp);
        state.cartTotalPrice += Number(state.CartItems[i].TotalPrice);
      }
      state.netPayable = Number(state.cartTotalAmount);
    },
    clearCartLocal(state, action) {
      state.CartItems = [];
      state.cartTotalQuantity = 0;
      state.cartTotalAmount = 0;
      state.netPayable = 0;
      state.cartTotalMrp = 0;
      state.cartPackQuantity = 0;
      setLocalStorage('CartItems', state.CartItems);
    },
    // Initialize cart from localStorage
    initializeCartFromLocalStorage(state) {
      const savedCartItems = getLocalStorage('CartItems');
      const savedProductTotal = getLocalStorage('ProductTotal');
      const savedSelectedCategory = getLocalStorage('selectedCategory');

      if (savedCartItems) {
        state.CartItems = savedCartItems;
        // Recalculate totals from saved cart items
        state.cartTotalQuantity = savedCartItems.reduce((total, item) => total + item.Qty, 0);
        state.cartTotalAmount = savedCartItems.reduce((total, item) => total + (item.TotalAmount || 0), 0);
        state.netPayable = state.cartTotalAmount;
        state.cartTotalMrp = savedCartItems.reduce((total, item) => total + (item.TotalMrp || 0), 0);
        state.cartPackQuantity = savedCartItems.length;
        
        // Calculate logistics amount
        if (state.cartTotalAmount >= 400) {
          state.logisticsAmount = 0;
        } else {
          state.logisticsAmount = 40;
        }
      }

      if (savedProductTotal) {
        state.ProductTotal = savedProductTotal;
      }

      if (savedSelectedCategory) {
        state.selectedCategory = savedSelectedCategory;
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Cart
      .addCase(fetchCartFromBackend.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCartFromBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.CartItems = action.payload.data.CartItems || [];
          // Recalculate totals from backend data
          state.cartTotalAmount = action.payload.data.cartTotalAmount || 0;
          state.netPayable = action.payload.data.netPayable || 0;
          state.cartTotalMrp = action.payload.data.cartTotalMrp || 0;
          state.cartTotalQuantity = action.payload.data.cartTotalQuantity || 0;
          state.cartPackQuantity = action.payload.data.cartPackQuantity || 0;

          console.log('🛒 Cart loaded from backend:', {
            items: state.CartItems.length,
            total: state.netPayable,
            quantity: state.cartTotalQuantity,
          });

          setLocalStorage('CartItems', state.CartItems);
        }
      })
      .addCase(fetchCartFromBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log('❌ Failed to fetch cart:', state.error);
      })
      // Add to Cart
      .addCase(addToCartBackend.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.CartItems = action.payload.data.CartItems || [];
          state.cartTotalAmount = action.payload.data.cartTotalAmount || 0;
          state.netPayable = action.payload.data.netPayable || 0;
          state.cartTotalMrp = action.payload.data.cartTotalMrp || 0;
          state.cartTotalQuantity = action.payload.data.cartTotalQuantity || 0;
          state.cartPackQuantity = action.payload.data.cartPackQuantity || 0;

          console.log('🛒 Cart updated after add:', {
            items: state.CartItems.length,
            total: state.netPayable,
            quantity: state.cartTotalQuantity,
          });

          setLocalStorage('CartItems', state.CartItems);
        }
      })
      .addCase(addToCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log('❌ Failed to add to cart:', state.error);
      })
      // Remove from Cart
      .addCase(removeFromCartBackend.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.CartItems = action.payload.data.CartItems || [];
          state.cartTotalAmount = action.payload.data.cartTotalAmount || 0;
          state.netPayable = action.payload.data.netPayable || 0;
          state.cartTotalMrp = action.payload.data.cartTotalMrp || 0;
          state.cartTotalQuantity = action.payload.data.cartTotalQuantity || 0;
          state.cartPackQuantity = action.payload.data.cartPackQuantity || 0;

          console.log('🛒 Cart updated after remove:', {
            items: state.CartItems.length,
            total: state.netPayable,
            quantity: state.cartTotalQuantity,
          });

          setLocalStorage('CartItems', state.CartItems);
        }
      })
      .addCase(removeFromCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log('❌ Failed to remove from cart:', state.error);
      })
      // Update Cart Item
      .addCase(updateCartItemBackend.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCartItemBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.CartItems = action.payload.data.CartItems || [];
          state.cartTotalAmount = action.payload.data.cartTotalAmount || 0;
          state.netPayable = action.payload.data.netPayable || 0;
          state.cartTotalMrp = action.payload.data.cartTotalMrp || 0;
          state.cartTotalQuantity = action.payload.data.cartTotalQuantity || 0;
          state.cartPackQuantity = action.payload.data.cartPackQuantity || 0;

          console.log('🛒 Cart updated after quantity change:', {
            items: state.CartItems.length,
            total: state.netPayable,
            quantity: state.cartTotalQuantity,
          });

          setLocalStorage('CartItems', state.CartItems);
        }
      })
      .addCase(updateCartItemBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log('❌ Failed to update cart item:', state.error);
      })
      // Clear Cart
      .addCase(clearCartBackend.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearCartBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.CartItems = [];
          state.cartTotalQuantity = 0;
          state.cartTotalAmount = 0;
          state.netPayable = 0;
          state.cartTotalMrp = 0;
          state.cartPackQuantity = 0;

          console.log('🛒 Cart cleared');

          setLocalStorage('CartItems', state.CartItems);
        }
      })
      .addCase(clearCartBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        console.log('❌ Failed to clear cart:', state.error);
      });
  },
});

export const {
  allProductUpdate,
  setSelectedCategory,
  setCheckoutAllData,
  addToCartLocal,
  decrementCartLocal,
  removeFromCartLocal,
  getCartTotal,
  clearCartLocal,
  initializeCartFromLocalStorage,
} = CartSlice.actions;

export default CartSlice.reducer;